<template>
  <div class="page-container">
    <Wrap>
      <Breadcrumbs
        :breadcrumbs="categoryStore.breadcrumbs"
        events-identifier="category"
        :track-event="trackBreadcrumbsEvent"
      />

      <h1 class="title-h1">{{ categoryStore.seoData?.h1 }}</h1>
      <p class="text-body">{{ categoryStore.seoData?.h2 }}</p>
    </Wrap>

    <FiltersBlock />

    <Tags />
    <Wrap>
      <ExperiencesList v-if="categoryStore.filters[EExperienceFilterType.SEO_DEFAULT]" />

      <ReviewsSlider v-if="categoryStore.reviewsApiFilters" />

      <ArticlesBlock v-if="categoryStore.filters[EExperienceFilterType.SEO_DEFAULT]" />

      <SeoBlock
        v-if="categoryStore.seoData?.text"
        :text="categoryStore.seoData.text"
      />

      <Links v-if="categoryStore.filters[EExperienceFilterType.SEO_DEFAULT]?.city" />
    </Wrap>
  </div>
</template>

<script lang="ts" setup>
  import { watch } from 'vue';
  import { Breadcrumbs, type TrackEvent } from '@experience/core-breadcrumbs-ui';
  import { useHead } from '@unhead/vue';
  import { onMounted } from 'vue';
  import { useRoute } from 'vue-router';

  import { trackEvent } from '@/services/analytics';

  import { useCategoryStore } from '@/shared/store';
  import Wrap from '@/shared/Wrap.vue';

  import Links from '@/features/Links.vue';
  import Tags from '@/features/Tags.vue';

  import { ArticlesBlock } from '@/widgets/articlesBlock';
  import { ExperiencesList } from '@/widgets/experiencesList';
  import { EExperienceFilterType, FiltersBlock } from '@/widgets/filtersBlock';
  import { ReviewsSlider } from '@/widgets/reviewsSlider';
  import { SeoBlock } from '@/widgets/seoBlock';

  const categoryStore = useCategoryStore();

  const trackBreadcrumbsEvent = trackEvent as TrackEvent;

  const route = useRoute();

  useHead({
    title: () => categoryStore.seoData?.title,
    meta: [
      {
        name: 'description',
        content: () => categoryStore.seoData?.description,
      },
      {
        property: 'og:title',
        content: () => categoryStore.seoData?.title,
      },
      {
        property: 'og:description',
        content: () => categoryStore.seoData?.description,
      },
      {
        content: () => categoryStore.seoData?.img,
        property: 'og:image',
      },
      {
        name: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        name: 'twitter:image',
        content: () => categoryStore.seoData?.img,
      },
    ],
    link: [{ rel: 'canonical', href: () => categoryStore.seoData?.canonicalUrl }],
  });

  onMounted(() => {
    categoryStore.init();
  });

  watch(
    () => route.fullPath,
    async () => {
      await categoryStore.onRouteChange();
    },
    { immediate: true },
  );
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography';
  @use '@experience/shared/dist/assets/adaptivity';

  .page-container {
    display: block;
    box-sizing: border-box;
    width: 100%;
    margin: 0 auto;
  }

  .title-h1 {
    position: relative;
    display: block;
    width: 100%;
    margin: var(--alias-space-32) 0 0 0;
    color: var(--alias-color-text-default);

    @include typography.base-900-850-extra;

    @include adaptivity.mobile {
      margin-top: var(--alias-space-24);

      @include typography.base-800-750-extra;
    }
  }

  .text-body {
    box-sizing: border-box;
    margin: 4px 0 0;
    padding-bottom: 8px;
    color: var(--alias-color-text-default);

    @include typography.base-400-400-regular;
  }
</style>
